import { rgb } from "d3-color";
import { formatHex, formatHex8, formatHsl, formatRgb } from "culori";
import convertRgbToHsluv from './hsluv/convertRgbToHsluv.js';
import convertHsluvToRgb from './hsluv/convertHsluvToRgb.js';
import { Color } from "./color.js";
import { clamp, clampAlpha, clampHue, toRGB } from "./helpers.js";
const K = 18;
const darker = 0.7;
const brighter = 1 / darker;
export class Hsluv extends Color {
    h;
    s;
    l;
    opacity;
    constructor(h, s, l, opacity) {
        super();
        if (typeof h.h === 'number' && typeof h.s === 'number' && typeof h.l === 'number' && (typeof h.opacity === 'number' || typeof h.opacity === 'undefined')) {
            this.h = h.h;
            this.s = h.s;
            this.l = h.l;
            this.opacity = h.opacity ?? 1;
        }
        else if (typeof h === 'number' && typeof s === 'number' && typeof l === 'number' && (typeof opacity === 'number' || typeof opacity === 'undefined')) {
            this.h = h;
            this.s = s;
            this.l = l;
            this.opacity = opacity ?? 1;
        }
        else {
            const tcRgb = toRGB(h);
            const culoriRgb = { mode: "rgb", alpha: tcRgb.a ?? 1, r: tcRgb.r / 255, g: tcRgb.g / 255, b: tcRgb.b / 255 };
            const culoriHsluv = convertRgbToHsluv(culoriRgb);
            this.h = culoriHsluv.h ?? 0;
            this.s = culoriHsluv.s;
            this.l = culoriHsluv.l;
            this.opacity = culoriHsluv.alpha ?? 1;
        }
    }
    culori() {
        return { mode: "hsluv", alpha: this.opacity ?? 1, h: this.h, s: this.s, l: this.l };
    }
    brighter(k) {
        k = k == null ? brighter : Math.pow(brighter, k);
        return new Hsluv(this.h, this.s, this.l * k, this.opacity);
    }
    darker(k) {
        k = k == null ? darker : Math.pow(darker, k);
        return new Hsluv(this.h, this.s, this.l * k, this.opacity);
    }
    rgb() {
        const culoriRgb = convertHsluvToRgb(this.culori());
        return rgb(255 * culoriRgb.r, 255 * culoriRgb.g, 255 * culoriRgb.b, this.opacity);
    }
    copy(values) {
        return values ? new Hsluv(values) : new Hsluv(this.h, this.s, this.l, this.opacity);
    }
    ;
    clamp() {
        return new Hsluv(clampHue(this.h), clamp(this.s), clamp(this.l), clampAlpha(this.opacity));
    }
    displayable() {
        return (0 <= this.s && this.s <= 1 || isNaN(this.s))
            && (0 <= this.l && this.l <= 1)
            && (0 <= this.opacity && this.opacity <= 1);
    }
    formatHsl() {
        return formatHsl(convertHsluvToRgb(this.culori()));
    }
    toString() {
        return this.formatRgb();
    }
    formatHex() {
        return formatHex(convertHsluvToRgb(this.culori()));
    }
    formatHex8() {
        return formatHex8(convertHsluvToRgb(this.culori()));
    }
    formatRgb() {
        return formatRgb(convertHsluvToRgb(this.culori()));
    }
    /**
     * @deprecated Use color.formatHex.
     */
    hex() {
        return this.formatHex8();
    }
    ;
}
const createHsluv = (h, s, l, opacity) => {
    return new Hsluv(h, s, l, opacity);
};
/**
 * An HSLUV (UV hsl) color factory function to create a HSLUV color object.
 */
export const hsluv = createHsluv;
